import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { supabase } from "../../../supabaseClient";
import InsightsBackButton from "../shared/InsightsBackButton";
import "./Checklist.css";

const normalize = (value) => String(value ?? "").trim();
const uniqueSorted = (values, numeric = false) => {
  const unique = [...new Set(values.filter((value) => value !== null && value !== undefined && normalize(value) !== ""))];
  return unique.sort((a, b) => (numeric ? Number(b) - Number(a) : String(a).localeCompare(String(b))));
};

const getSubSets = (summary) => {
  if (Array.isArray(summary?.sub_sets)) return summary.sub_sets.filter(Boolean);
  if (summary?.sub_set) return [summary.sub_set];
  return [];
};

const checklistDisplayTitle = (entry) =>
  normalize(entry?.family_name) || normalize(entry?.set_name) || normalize(entry?.brand) || "Checklist";

const checklistDisplayBrand = (entry) => normalize(entry?.brand);
const getReleases = (entry) => Array.isArray(entry?.releases) ? entry.releases : [];
const releaseLabel = (release) => normalize(release?.release_name) || "Base";

// Keep checklist navigation focused on substantial product divisions rather than
// every insert/autograph/relic child release. No catalog schema changes required.
const MAJOR_RELEASE_MIN_ITEMS = 300;
const isMajorRelease = (release) => {
  const name = releaseLabel(release).toLowerCase();
  const itemCount = Number(release?.item_count || 0);

  if (name === "base") return true;
  if (/^series\s+\d+$/.test(name)) return true;
  if (name === "update" || name === "update series") return true;
  if (name === "logofractor") return true;

  return itemCount >= MAJOR_RELEASE_MIN_ITEMS;
};
const getMajorReleases = (entry) => getReleases(entry).filter(isMajorRelease);

const trackedReleaseLabel = (tracked, summaries) => {
  if (!tracked?.release_id) return "";
  const summary = summaries.find((entry) => Number(entry.family_id) === Number(tracked.release_family_id));
  const release = getReleases(summary).find((entry) => Number(entry.release_id) === Number(tracked.release_id));
  return releaseLabel(release);
};

const formatDetails = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, val]) => val !== null && val !== undefined && val !== "")
      .map(([key, val]) => `${key}: ${typeof val === "object" ? JSON.stringify(val) : val}`)
      .join(" · ");
  }
  return String(value);
};

export default function Checklist({ user }) {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [items, setItems] = useState([]);
  const [ownedIds, setOwnedIds] = useState(new Set());
  const [trackedChecklists, setTrackedChecklists] = useState([]);
  const [trackedProgress, setTrackedProgress] = useState({});
  const [trackedLoading, setTrackedLoading] = useState(false);
  const [trackingBusy, setTrackingBusy] = useState(false);

  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [year, setYear] = useState("");
  const [brand, setBrand] = useState("");
  const [setName, setSetName] = useState("");
  const [subSet, setSubSet] = useState("");
  const [search, setSearch] = useState("");
  const [summarySearch, setSummarySearch] = useState("");
  const [viewMode, setViewMode] = useState("all");
  const [highlightOwned, setHighlightOwned] = useState(true);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [selectedReleaseId, setSelectedReleaseId] = useState("");
  const [showAllInsertSubsets, setShowAllInsertSubsets] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const trackedKey = (entry) => [
    entry.category_id || "",
    entry.subcategory_id || "",
    entry.year ?? "",
    normalize(entry.brand).toLowerCase(),
    normalize(entry.set_name).toLowerCase(),
    normalize(entry.sub_set).toLowerCase(),
  ].join("|");

  const loadTrackedChecklists = async () => {
    if (!user?.id) {
      setTrackedChecklists([]);
      setTrackedProgress({});
      return;
    }

    setTrackedLoading(true);
    try {
      const { data: tracked, error: trackedError } = await supabase
        .from("user_tracked_checklists")
        .select("id, user_id, category_id, subcategory_id, year, brand, set_name, sub_set, release_family_id, release_id, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (trackedError) throw trackedError;

      const trackedRows = tracked || [];
      setTrackedChecklists(trackedRows);

      if (!trackedRows.length) {
        setTrackedProgress({});
        return;
      }

      const { data: ownedRows, error: ownedError } = await supabase
        .from("user_items")
        .select("item_id")
        .eq("user_id", user.id)
        .not("item_id", "is", null);

      if (ownedError) throw ownedError;
      const owned = new Set((ownedRows || []).map((row) => row.item_id).filter(Boolean));

      const progressEntries = await Promise.all(
        trackedRows.map(async (trackedRow) => {
          if (!trackedRow.release_family_id) return [trackedRow.id, { total: 0, owned: 0, missing: 0, percent: 0 }];
          const { data, error } = await supabase.rpc("get_checklist_items_v3", {
            p_family_id: trackedRow.release_family_id,
            p_release_id: trackedRow.release_id || null,
            p_sub_set: trackedRow.sub_set || null,
          });

          if (error) throw error;
          const checklistItems = data || [];
          const ownedCount = checklistItems.reduce(
            (count, item) => count + (owned.has(item.id) ? 1 : 0),
            0
          );
          const totalCount = checklistItems.length;

          return [trackedRow.id, {
            total: totalCount,
            owned: ownedCount,
            missing: Math.max(totalCount - ownedCount, 0),
            percent: totalCount ? Math.round((ownedCount / totalCount) * 100) : 0,
          }];
        })
      );

      setTrackedProgress(Object.fromEntries(progressEntries));
    } catch (error) {
      console.error("Tracked checklists failed:", error);
      setErrorMessage("We couldn't load your tracked checklists. Please try again.");
    } finally {
      setTrackedLoading(false);
    }
  };

  useEffect(() => {
    loadTrackedChecklists();
  }, [user?.id]);

  useEffect(() => {
    const loadCategories = async () => {
      const { data, error } = await supabase.from("categories").select("id, name").order("name");
      if (error) {
        console.error("Checklist categories failed:", error);
        setErrorMessage("We couldn't load checklist categories.");
        return;
      }
      setCategories(data || []);
    };
    loadCategories();
  }, []);

  useEffect(() => {
    setSubcategoryId("");
    setSubcategories([]);
    if (!categoryId) return;

    const loadSubcategories = async () => {
      const { data, error } = await supabase
        .from("subcategories")
        .select("id, name")
        .eq("category_id", categoryId)
        .order("name");
      if (error) {
        console.error("Checklist subcategories failed:", error);
        return;
      }
      setSubcategories(data || []);
    };
    loadSubcategories();
  }, [categoryId]);

  useEffect(() => {
    setSummaries([]);
    setSelectedSummary(null);
    setItems([]);
    setYear("");
    setBrand("");
    setSetName("");
    setSubSet("");
    setSummarySearch("");
    setErrorMessage("");

    if (!categoryId) return;

    const loadSummaries = async () => {
      setLoading(true);
      try {
        const response = await supabase.rpc("get_checklist_summary_v3", {
          p_category_id: categoryId,
          p_subcategory_id: subcategoryId || null,
        });

        if (response.error) throw response.error;
        setSummaries(response.data || []);
      } catch (error) {
        console.error("Checklist summaries failed:", error);
        setErrorMessage("We couldn't load the available checklists. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadSummaries();
  }, [categoryId, subcategoryId]);

  const availableYears = useMemo(() => uniqueSorted(summaries.map((s) => s.year), true), [summaries]);

  const availableBrands = useMemo(() => {
    const source = year ? summaries.filter((s) => String(s.year) === String(year)) : summaries;
    return uniqueSorted(source.map((s) => s.brand));
  }, [summaries, year]);

  const availableSets = useMemo(() => {
    let source = summaries;
    if (year) source = source.filter((s) => String(s.year) === String(year));
    if (brand) source = source.filter((s) => normalize(s.brand) === brand);
    return uniqueSorted(source.map((s) => s.family_name));
  }, [summaries, year, brand]);

  const availableSubSets = useMemo(() => {
    let source = summaries;
    if (year) source = source.filter((s) => String(s.year) === String(year));
    if (brand) source = source.filter((s) => normalize(s.brand) === brand);
    if (setName) source = source.filter((s) => normalize(s.family_name) === setName);
    return uniqueSorted(source.flatMap(getSubSets));
  }, [summaries, year, brand, setName]);

  useEffect(() => {
    if (brand && !availableBrands.includes(brand)) setBrand("");
  }, [availableBrands, brand]);

  useEffect(() => {
    if (setName && !availableSets.includes(setName)) setSetName("");
  }, [availableSets, setName]);

  useEffect(() => {
    if (subSet && !availableSubSets.includes(subSet)) setSubSet("");
  }, [availableSubSets, subSet]);

  const displayedSummaries = useMemo(() => {
    const needle = summarySearch.trim().toLowerCase();
    return summaries.filter((summary) => {
      if (year && String(summary.year) !== String(year)) return false;
      if (brand && normalize(summary.brand) !== brand) return false;
      if (setName && normalize(summary.family_name) !== setName) return false;
      if (subSet && !getSubSets(summary).includes(subSet)) return false;
      if (!needle) return true;
      return [summary.year, summary.brand, summary.family_name, ...getReleases(summary).map(releaseLabel), ...getSubSets(summary)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [summaries, year, brand, setName, subSet, summarySearch]);

  const fetchChecklistItems = async (summary, options = {}) => {
    setLoading(true);
    setErrorMessage("");
    const releaseId = options.releaseId ?? null;
    try {
      const response = await supabase.rpc("get_checklist_items_v3", {
        p_family_id: summary.family_id,
        p_release_id: releaseId,
        p_sub_set: options.subSet ?? (subSet || null),
      });
      if (response.error) throw response.error;

      let ownedQuery = supabase.from("user_items").select("item_id").not("item_id", "is", null);
      if (user?.id) ownedQuery = ownedQuery.eq("user_id", user.id);
      const ownedResponse = await ownedQuery;
      if (ownedResponse.error) throw ownedResponse.error;

      setOwnedIds(new Set((ownedResponse.data || []).map((row) => row.item_id).filter(Boolean)));
      setItems(response.data || []);
      setSelectedSummary(summary);
      setSelectedReleaseId(releaseId ? String(releaseId) : "");
      if (!selectedSummary || Number(selectedSummary.family_id) !== Number(summary.family_id)) {
        setShowAllInsertSubsets(false);
      }
      if (Object.prototype.hasOwnProperty.call(options, "subSet")) {
        setSubSet(normalize(options.subSet));
      }
      setSearch("");
      setViewMode(options.viewMode || "all");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Checklist items failed:", error);
      setErrorMessage("We couldn't load that checklist. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const changeRelease = async (value) => {
    if (!selectedSummary) return;
    await fetchChecklistItems(selectedSummary, {
      releaseId: value ? Number(value) : null,
      subSet: null,
      viewMode,
    });
  };

  const changeInsertSubset = async (value) => {
    if (!selectedSummary) return;
    const nextSubSet = normalize(value);
    setSubSet(nextSubSet);
    await fetchChecklistItems(selectedSummary, {
      releaseId: selectedReleaseId ? Number(selectedReleaseId) : null,
      subSet: nextSubSet || null,
      viewMode,
    });
  };

  const activeTrackedChecklist = useMemo(() => {
    if (!selectedSummary) return null;
    return trackedChecklists.find((entry) =>
      Number(entry.release_family_id) === Number(selectedSummary.family_id) &&
      String(entry.release_id || "") === String(selectedReleaseId || "") &&
      normalize(entry.sub_set) === normalize(subSet)
    ) || null;
  }, [trackedChecklists, selectedSummary, selectedReleaseId, subSet]);

  const trackCurrentChecklist = async () => {
    if (!user?.id || !selectedSummary || trackingBusy) return;
    setTrackingBusy(true);
    setErrorMessage("");
    try {
      const { error } = await supabase.from("user_tracked_checklists").insert({
        user_id: user.id,
        category_id: categoryId,
        subcategory_id: subcategoryId || null,
        year: selectedSummary.year ?? null,
        brand: selectedSummary.brand,
        set_name: normalize(selectedSummary.family_name) || null,
        sub_set: normalize(subSet) || null,
        release_family_id: selectedSummary.family_id,
        release_id: selectedReleaseId ? Number(selectedReleaseId) : null,
      });
      if (error && error.code !== "23505") throw error;
      await loadTrackedChecklists();
    } catch (error) {
      console.error("Track checklist failed:", error);
      setErrorMessage("We couldn't track this checklist. Please try again.");
    } finally {
      setTrackingBusy(false);
    }
  };

  const stopTrackingChecklist = async (trackedId) => {
    if (!trackedId || trackingBusy) return;
    setTrackingBusy(true);
    setErrorMessage("");
    try {
      const { error } = await supabase
        .from("user_tracked_checklists")
        .delete()
        .eq("id", trackedId);
      if (error) throw error;
      await loadTrackedChecklists();
    } catch (error) {
      console.error("Stop tracking checklist failed:", error);
      setErrorMessage("We couldn't stop tracking this checklist. Please try again.");
    } finally {
      setTrackingBusy(false);
    }
  };

  const openTrackedChecklist = async (tracked, mode = "all") => {
    setCategoryId(String(tracked.category_id));
    setSubcategoryId(tracked.subcategory_id ? String(tracked.subcategory_id) : "");
    setYear(tracked.year == null ? "" : String(tracked.year));
    setBrand(normalize(tracked.brand));
    setSetName(normalize(tracked.set_name));
    setSubSet(normalize(tracked.sub_set));

    const summary = summaries.find((s) => Number(s.family_id) === Number(tracked.release_family_id)) || {
      family_id: tracked.release_family_id, year: tracked.year, brand: tracked.brand, family_name: tracked.set_name, releases: []
    };
    await fetchChecklistItems(summary, {
      releaseId: tracked.release_id || null,
      viewMode: mode,
      subSet: tracked.sub_set || null,
    });
  };

  const visibleItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      const isOwned = ownedIds.has(item.id);
      if (viewMode === "owned" && !isOwned) return false;
      if (viewMode === "missing" && isOwned) return false;
      if (subSet && normalize(item.sub_set) && normalize(item.sub_set) !== subSet) return false;
      if (!needle) return true;
      return [item.card_number, item.name, item.sub_set, item.brand, item.set_name, formatDetails(item.additional_details)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [items, ownedIds, viewMode, search, subSet]);

  const ownedCount = useMemo(() => items.reduce((count, item) => count + (ownedIds.has(item.id) ? 1 : 0), 0), [items, ownedIds]);
  const totalCount = items.length;
  const completion = totalCount ? Math.round((ownedCount / totalCount) * 100) : 0;

  const categoryName = categories.find((c) => String(c.id) === String(categoryId))?.name || "";
  const subcategoryName = subcategories.find((s) => String(s.id) === String(subcategoryId))?.name || "";

  const exportToExcel = (missingOnly = false) => {
    const source = missingOnly ? items.filter((item) => !ownedIds.has(item.id)) : items;
    if (!source.length) return;

    const exportData = source.map((item) => ({
      Category: categoryName,
      Subcategory: subcategoryName,
      Year: item.year ?? selectedSummary?.year ?? "",
      Brand: item.brand ?? selectedSummary?.brand ?? "",
      Set: selectedSummary?.family_name ?? item.set_name ?? "",
      Subset: item.release_name ?? "",
      "Insert/Subset": item.sub_set ?? "",
      "Item/Card #": item.card_number ?? "",
      Name: item.name ?? "",
      Owned: ownedIds.has(item.id) ? "Yes" : "No",
      "Additional Details": formatDetails(item.additional_details),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    worksheet["!cols"] = [18, 18, 10, 18, 28, 24, 24, 14, 32, 10, 45].map((wch) => ({ wch }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Checklist");

    const safeName = [selectedSummary?.year, selectedSummary?.family_name, selectedReleaseId ? items[0]?.release_name : "master-checklist", missingOnly ? "missing" : "checklist"]
      .filter(Boolean)
      .join("-")
      .replace(/[^a-z0-9-_]+/gi, "-")
      .replace(/-+/g, "-");
    XLSX.writeFile(workbook, `${safeName || "hobbli-checklist"}.xlsx`);
  };

  const resetFilters = () => {
    setYear("");
    setBrand("");
    setSetName("");
    setSubSet("");
    setSummarySearch("");
  };

  if (selectedSummary) {
    return (
      <section className="checklist-page">
        <div className="checklist-shell">
          <div className="checklist-top-nav">
            <InsightsBackButton />
            <button className="checklist-back" onClick={() => { setSelectedSummary(null); setItems([]); }}>
              ← Back to checklists
            </button>
          </div>

          <div className="checklist-detail-hero">
            <div>
              <div className="checklist-eyebrow">Hobbli Checklists</div>
              <h1>{[selectedSummary.year, selectedSummary.family_name].filter(Boolean).join(" ")}</h1>
              <p>
                {subcategoryName || categoryName}
                {subSet ? ` · ${subSet}` : ""}
              </p>
              {user?.id && (
                <div className="checklist-track-actions">
                  {activeTrackedChecklist ? (
                    <>
                      <span className="checklist-tracked-pill">✓ In Sets I’m Building</span>
                      <button
                        className="checklist-button secondary compact"
                        onClick={() => stopTrackingChecklist(activeTrackedChecklist.id)}
                        disabled={trackingBusy}
                      >
                        Stop Tracking
                      </button>
                    </>
                  ) : (
                    <button
                      className="checklist-button secondary compact"
                      onClick={trackCurrentChecklist}
                      disabled={trackingBusy}
                    >
                      {trackingBusy ? "Saving…" : `＋ Track ${subSet ? "This Insert/Subset" : selectedReleaseId ? "This Release" : "Master Checklist"}`}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="checklist-progress-card">
              <div className="checklist-progress-top">
                <strong>{ownedCount} / {totalCount}</strong>
                <span>{completion}% complete</span>
              </div>
              <div className="checklist-progress-track" aria-label={`${completion}% complete`}>
                <span style={{ width: `${completion}%` }} />
              </div>
              <div className="checklist-progress-meta">
                <span>{ownedCount} owned</span>
                <span>{Math.max(totalCount - ownedCount, 0)} missing</span>
              </div>
            </div>
          </div>

          {getMajorReleases(selectedSummary).length > 0 && (
            <div className="checklist-release-nav">
              <span>Checklist</span>
              <button className={!selectedReleaseId ? "active" : ""} onClick={() => changeRelease("")}>Master Checklist ({selectedSummary.item_count})</button>
              {getMajorReleases(selectedSummary).map((release) => (
                <button key={release.release_id} className={String(selectedReleaseId) === String(release.release_id) ? "active" : ""} onClick={() => changeRelease(String(release.release_id))}>
                  {releaseLabel(release)} ({release.item_count})
                </button>
              ))}
            </div>
          )}

          {!!getSubSets(selectedSummary).length && (() => {
            const allInsertSubsets = getSubSets(selectedSummary);
            const collapsedLimit = 12;
            let visibleInsertSubsets = showAllInsertSubsets
              ? allInsertSubsets
              : allInsertSubsets.slice(0, collapsedLimit);

            if (
              subSet &&
              !showAllInsertSubsets &&
              !visibleInsertSubsets.some((value) => normalize(value) === normalize(subSet))
            ) {
              visibleInsertSubsets = [...visibleInsertSubsets.slice(0, collapsedLimit - 1), subSet];
            }

            const hasMore = allInsertSubsets.length > collapsedLimit;

            return (
              <section className="checklist-insert-filter" aria-label="Filter checklist by insert or subset">
                <div className="checklist-insert-filter-header">
                  <div>
                    <span className="checklist-insert-filter-title">Inserts / Subsets</span>
                    <span className="checklist-insert-filter-count">{allInsertSubsets.length} available</span>
                  </div>
                  {hasMore && (
                    <button
                      type="button"
                      className="checklist-insert-toggle"
                      onClick={() => setShowAllInsertSubsets((current) => !current)}
                    >
                      {showAllInsertSubsets ? "Show fewer" : `Show all ${allInsertSubsets.length}`}
                    </button>
                  )}
                </div>

                <div className="checklist-insert-chips">
                  <button
                    type="button"
                    className={`checklist-insert-chip ${!subSet ? "active" : ""}`}
                    onClick={() => changeInsertSubset("")}
                  >
                    All Cards
                  </button>
                  {visibleInsertSubsets.map((value) => (
                    <button
                      type="button"
                      key={value}
                      className={`checklist-insert-chip ${normalize(subSet) === normalize(value) ? "active" : ""}`}
                      onClick={() => changeInsertSubset(value)}
                      title={value}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </section>
            );
          })()}

          <div className="checklist-toolbar">
            <div className="checklist-segmented" role="group" aria-label="Checklist view">
              {[["all", "All"], ["owned", "Owned"], ["missing", "Missing"]].map(([value, label]) => (
                <button key={value} className={viewMode === value ? "active" : ""} onClick={() => setViewMode(value)}>
                  {label}
                </button>
              ))}
            </div>
            <div className="checklist-toolbar-search">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, number, insert/subset, details..." />
            </div>
            <label className="checklist-toggle">
              <input type="checkbox" checked={highlightOwned} onChange={(e) => setHighlightOwned(e.target.checked)} />
              <span>Highlight owned</span>
            </label>
            <button className="checklist-button secondary" onClick={() => exportToExcel(false)}>Export Excel</button>
            <button className="checklist-button secondary" onClick={() => exportToExcel(true)} disabled={ownedCount === totalCount}>Export Missing</button>
          </div>

          {errorMessage && <div className="checklist-error">{errorMessage}</div>}
          {loading ? <div className="checklist-loading">Loading checklist…</div> : (
            <div className="checklist-table-card">
              <div className="checklist-result-count">Showing {visibleItems.length} of {totalCount} items</div>
              <div className="checklist-table-wrap">
                <table className="checklist-table">
                  <thead><tr><th>#</th><th>Item</th>{!selectedReleaseId && getReleases(selectedSummary).length > 1 && <th>Subset</th>}<th>Insert</th><th>Status</th><th>Details</th></tr></thead>
                  <tbody>
                    {visibleItems.map((item) => {
                      const isOwned = ownedIds.has(item.id);
                      return (
                        <tr key={item.id} className={highlightOwned && isOwned ? "owned-row" : ""}>
                          <td className="number-cell">{item.card_number || "—"}</td>
                          <td><strong>{item.name || "Unnamed item"}</strong></td>
                          {!selectedReleaseId && getReleases(selectedSummary).length > 1 && <td>{item.release_name || "Base"}</td>}
                          <td>{item.sub_set || "—"}</td>
                          <td><span className={`ownership-pill ${isOwned ? "owned" : "missing"}`}>{isOwned ? "✓ Owned" : "Missing"}</span></td>
                          <td className="details-cell">{formatDetails(item.additional_details) || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!visibleItems.length && <div className="checklist-empty">No items match this view.</div>}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="checklist-page">
      <div className="checklist-shell">
        <InsightsBackButton />
        <div className="checklist-hero">
          <div>
            <div className="checklist-eyebrow">Explore · Track · Complete</div>
            <h1>Hobbli Checklists</h1>
            <p>Explore the Hobbli catalog, find the sets and items you care about, and see how your collection stacks up.</p>
          </div>
          <div className="checklist-hero-stat">
            <span>Available checklists</span>
            <strong>{summaries.length || "—"}</strong>
            <small>{categoryId ? "for your current category" : "choose a category to begin"}</small>
          </div>
        </div>

        {user?.id && (
          <section className="checklist-tracked-section">
            <div className="checklist-section-title">
              <div>
                <div className="checklist-eyebrow">Your collecting goals</div>
                <h2>Sets I’m Building</h2>
                <p>Track a master checklist, release, or individual insert/subset and Hobbli keeps its completion up to date from your collection.</p>
              </div>
            </div>

            {trackedLoading ? (
              <div className="checklist-loading compact">Loading your sets…</div>
            ) : trackedChecklists.length ? (
              <div className="checklist-tracked-grid">
                {trackedChecklists.map((tracked) => {
                  const progress = trackedProgress[tracked.id] || { total: 0, owned: 0, missing: 0, percent: 0 };
                  return (
                    <article className="checklist-tracked-card" key={tracked.id}>
                      <div className="checklist-set-card-top">
                        <span className="checklist-year-badge">{tracked.year || "Year N/A"}</span>
                        <span className="checklist-percent">{progress.percent}% complete</span>
                      </div>
                      <h3>{checklistDisplayTitle(tracked)}{trackedReleaseLabel(tracked, summaries) ? ` — ${trackedReleaseLabel(tracked, summaries)}` : ""}</h3>
                      {checklistDisplayBrand(tracked) && (
                        <p className="checklist-brand">{checklistDisplayBrand(tracked)}</p>
                      )}
                      {!tracked.release_id && !tracked.sub_set && <p className="checklist-subsets">Master Checklist</p>}
                      {tracked.release_id && !tracked.sub_set && <p className="checklist-subsets">Release</p>}
                      {tracked.sub_set && <p className="checklist-subsets">Insert · {tracked.sub_set}</p>}

                      <div className="checklist-tracked-progress-copy">
                        <strong>{progress.owned} / {progress.total}</strong>
                        <span>{progress.missing} missing</span>
                      </div>
                      <div className="checklist-mini-progress">
                        <span style={{ width: `${progress.percent}%` }} />
                      </div>

                      <div className="checklist-tracked-actions">
                        <button className="checklist-button primary" onClick={() => openTrackedChecklist(tracked, "all")}>View Checklist</button>
                        <button className="checklist-button secondary" onClick={() => openTrackedChecklist(tracked, "missing")} disabled={progress.missing === 0}>View Missing</button>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="checklist-tracked-empty">
                <strong>No tracked sets yet.</strong>
                <span>Open a master checklist, release, or insert/subset below and choose Track to add it here.</span>
              </div>
            )}
          </section>
        )}

        <div className="checklist-filter-card">
          <div className="checklist-filter-heading">
            <div><h2>Find a checklist</h2><p>Start broad or narrow the catalog down to exactly what you want to explore.</p></div>
            {(year || brand || setName || subSet || summarySearch) && <button className="checklist-clear" onClick={resetFilters}>Clear filters</button>}
          </div>

          <div className="checklist-filter-grid">
            <label><span>Category</span><select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}><option value="">Choose category</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
            <label><span>Subcategory</span><select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)} disabled={!categoryId}><option value="">All subcategories</option>{subcategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
            <label><span>Year</span><select value={year} onChange={(e) => setYear(e.target.value)} disabled={!summaries.length}><option value="">All years</option>{availableYears.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
            <label><span>Brand</span><select value={brand} onChange={(e) => setBrand(e.target.value)} disabled={!summaries.length}><option value="">All brands</option>{availableBrands.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
            <label><span>Set</span><select value={setName} onChange={(e) => setSetName(e.target.value)} disabled={!summaries.length}><option value="">All sets</option>{availableSets.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
            <label><span>Insert/Subset</span><select value={subSet} onChange={(e) => setSubSet(e.target.value)} disabled={!availableSubSets.length}><option value="">All subsets</option>{availableSubSets.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
          </div>

          <div className="checklist-catalog-search">
            <input value={summarySearch} onChange={(e) => setSummarySearch(e.target.value)} placeholder="Search available checklists (e.g. T206, Topps, Prizm)..." disabled={!categoryId} />
          </div>
        </div>

        {errorMessage && <div className="checklist-error">{errorMessage}</div>}

        {!categoryId ? (
          <div className="checklist-welcome-card"><div className="checklist-welcome-icon">✓</div><h2>Start exploring the catalog</h2><p>Choose a category above. Hobbli will show the checklists currently available in the canonical catalog.</p></div>
        ) : loading ? (
          <div className="checklist-loading">Finding checklists…</div>
        ) : (
          <>
            <div className="checklist-section-title"><div><h2>Available Checklists</h2><p>{displayedSummaries.length} {displayedSummaries.length === 1 ? "checklist" : "checklists"} match your filters</p></div></div>
            <div className="checklist-card-grid">
              {displayedSummaries.map((summary, index) => {
                const total = Number(summary.item_count || 0);
                const owned = Number(summary.owned_count || 0);
                const percent = total ? Math.round((owned / total) * 100) : 0;
                return (
                  <article className="checklist-set-card" key={summary.family_id || `${summary.year}-${summary.brand}-${index}`}>
                    <div className="checklist-set-card-top"><span className="checklist-year-badge">{summary.year || "Year N/A"}</span>{owned > 0 && <span className="checklist-percent">{percent}% complete</span>}</div>
                    <h3>{checklistDisplayTitle(summary)}</h3>
                    {checklistDisplayBrand(summary) && (
                      <p className="checklist-brand">{checklistDisplayBrand(summary)}</p>
                    )}
                    {getMajorReleases(summary).filter((release) => releaseLabel(release).toLowerCase() !== "base").length > 0 && (
                      <div className="checklist-release-list">
                        {getMajorReleases(summary)
                          .filter((release) => releaseLabel(release).toLowerCase() !== "base")
                          .map((release) => <span key={release.release_id}>{releaseLabel(release)} · {release.item_count}</span>)}
                      </div>
                    )}
                    {(() => {
                      const insertSubsetCount = getReleases(summary).filter((release) => !isMajorRelease(release)).length + getSubSets(summary).length;
                      return insertSubsetCount > 0
                        ? <p className="checklist-subsets">Includes {insertSubsetCount} Inserts / Subsets</p>
                        : null;
                    })()}
                    <div className="checklist-set-stats"><div><strong>{total}</strong><span>items</span></div><div><strong>{owned}</strong><span>owned</span></div><div><strong>{Math.max(total - owned, 0)}</strong><span>missing</span></div></div>
                    <div className="checklist-mini-progress"><span style={{ width: `${percent}%` }} /></div>
                    <button className="checklist-button primary" onClick={() => fetchChecklistItems(summary)}>View Master Checklist <span>→</span></button>
                  </article>
                );
              })}
            </div>
            {!displayedSummaries.length && <div className="checklist-welcome-card"><h2>No checklists match those filters</h2><p>Try clearing one or more filters to broaden the catalog.</p><button className="checklist-button secondary" onClick={resetFilters}>Clear filters</button></div>}
          </>
        )}

        <div className="checklist-note"><strong>About checklist data</strong><span>Hobbli checklists are built from the canonical catalog and continue to improve as catalog data is reviewed and expanded.</span></div>
      </div>
    </section>
  );
}
